<template>
  <div
    class="flex flex-col w-full overflow-y-auto px-3 lg:px-0 lg:w-[700px] relative items-center"
  >
    <Suspense>
      <template #default>
        <component
          :is="currentComponent"
          :key="item.id"
          :isLoading="isLoading"
          :error="err"
          @answer="updateAnswer"
          @next="next"
          @skip="skip"
          :item="item"
        />
      </template>
      <template #fallback>
        <div class="flex flex-col items-center justify-center w-full mt-64">
          <Spinner class="w-12 h-12" text-color="text-fire" />
        </div>
      </template>
    </Suspense>
  </div>
  <TransitionRoot
    as="template"
    :show="showAllQuestion"
    enter="transition transform ease-out duration-300"
    enter-from="opacity-0 -translate-x-1/2"
    enter-to="opacity-100 translate-x-0"
    leave="transition transform ease-in duration-600"
    leave-from="opacity-100 translate-x-0"
    leave-to="opacity-0 -translate-x-1/2"
  >
    <all-questions
      @selection="setItem"
      @close="showAllQuestion = false"
      :current="current"
      class="absolute z-50 bg-gradient-to-b from-[#f6f6f7] via-[#fafafa] to-[#f6f6f6] dark:from-[#22212a] dark:via-[#262630] dark:to-[#23212e] top-0 right-0 flex w-[95%] md:w-[60%] lg:w-[45%] xl:w-[40%] min-h-screen px-8"
      :items.sync="items"
    />
  </TransitionRoot>
  <TransitionRoot
    as="template"
    :show="showAllDocuments"
    enter="transition transform ease-out duration-300"
    enter-from="opacity-0 -translate-x-1/2"
    enter-to="opacity-100 translate-x-0"
    leave="transition transform ease-in duration-600"
    leave-from="opacity-100 translate-x-0"
    leave-to="opacity-0 -translate-x-1/2"
  >
    <all-documents
      @selection="setItem"
      @close="showAllDocuments = false"
      @update="refreshFiles"
      class="absolute z-50 bg-gradient-to-b from-[#f6f6f7] via-[#fafafa] to-[#f6f6f6] dark:from-[#22212a] dark:via-[#262630] dark:to-[#23212e] top-0 right-0 flex w-[95%] md:w-[60%] lg:w-[45%] xl:w-[40%] min-h-screen px-8"
      :items.sync="files"
    />
  </TransitionRoot>
  <FooterComponent @toggle="onToggle" />
</template>

<script>
import {
  computed,
  onErrorCaptured,
  onMounted,
  reactive,
  ref,
  toRefs,
} from "vue";
import _ from "lodash";
import AllQuestions from "./AllQuestions";
import AllDocuments from "./AllDocuments";
import Spinner from "./Spinner";
import { TransitionRoot } from "@headlessui/vue";
import FooterComponent from "./FooterComponent";

export default {
  name: "Questions",
  components: {
    FooterComponent,
    AllDocuments,
    AllQuestions,
    Spinner,
    TransitionRoot,
  },
  setup() {
    const state = reactive({
      showAllQuestion: false,
      showAllDocuments: false,
      showAllMessages: false,
      current: 1,
      items: [],
      files: [],
      isLoading: false,
      err: {},
    });
    const item = ref({});
    const error = ref(null);

    const onToggle = (type, value) => {
      if (type === "showAllQuestion") {
        state.showAllQuestion = true;
        resetSliderState("question");
      }
      if (type === "showAllDocuments") {
        state.showAllDocuments = true;
        resetSliderState("doc");
      }
      if (type === "showAllMessages") {
        state.showAllMessages = true;
        resetSliderState("msg");
      }
    };

    const resetSliderState = (current) => {
      switch (current) {
        case "doc":
          state.showAllQuestion = false;
          state.showAllMessages = false;
          break;
        case "question":
          state.showAllDocuments = false;
          state.showAllMessages = false;
          break;
        case "msg":
          state.showAllDocuments = false;
          state.showAllQuestion = false;
          break;
      }
    };

    const currentComponent = computed(() => {
      switch (item.value.type) {
        case "range":
          return "Range";
        case "number":
          return "input-number";
        case "file":
          return "input-file";
        case "location":
          return "input-location";
        case "picture":
          return "input-picture";
        case "autocomplete":
          return "input-autocomplete";
        case "social":
          return "input-social";
        case "text":
          return "input-text";
        case "radio":
          return "input-radio";
        case "multi-select":
          return "input-multi";
        case "pdf_download_upload":
          return "input-fileio";
        default:
          return "input-text";
      }
    });

    onErrorCaptured((e) => {
      error.value = e;
      return true;
    });

    onMounted(async () => {
      try {
        const res = await axios.get("/questions");
        const files = await axios.get("/applications/files");
        state.items = _.each(res.data, (obj, idx) => {
          obj.value = "";
          if (obj.id === idx + 1) {
            state.current = idx;
            item.value.current = true;
            item.value = obj;
          } else {
            item.value.current = false;
          }
        });
        state.files = files.data;
      } catch (err) {
      } finally {
        state.isLoading = false;
      }
    });

    const setItem = (obj, index) => {
      item.value = obj;
      state.current = index;
    };

    const next = async (obj, selection, skip = false) => {
      try {
        if (!skip) {
          state.isLoading = true;
          await update(obj, selection);
        }

        state.current++;

        check();
        item.value = state.items[state.current];
      } catch (err) {
        state.err = err;
      } finally {
        state.isLoading = false;
      }
    };

    const update = async (question, selection) => {
      const data = {
        id: question.id,
        key: question.key,
        value: selection,
      };
      try {
        const res = await axios.put("/answers", data);
        const objIndex = state.items.findIndex((obj) => obj.id === question.id);
        state.items[objIndex]["answer"] = res.data;
      } catch (err) {
        throw err;
      }
    };

    const updateAnswer = (answer) => {
      const objIndex = state.items.findIndex((obj) => obj.id === answer.id);
      state.items[objIndex] = answer;
      item.value = answer;
    };

    const check = () => {
      if (state.current === state.items.length) {
        window.location.href = "/dashboard";
      }
    };

    const skip = (obj) => {
      state.current++;
      check();
      item.value = state.items[state.current];
    };

    const refreshFiles = async () => {
      const files = await axios.get("/applications/files");
      state.files = files.data;
    };

    return {
      currentComponent,
      onToggle,
      updateAnswer,
      check,
      skip,
      refreshFiles,
      item,
      error,
      next,
      setItem,
      ...toRefs(state),
    };
  },
};
</script>
